import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ToastContext, ToastVariant } from '../../hooks/useToast';

interface ToastItem {
  id: string;
  message: string;
  variant: ToastVariant;
}

interface ToastProps {
  toast: ToastItem;
  onDismiss: (id: string) => void;
}

interface ToastProviderProps {
  children: ReactNode;
}

const VARIANT_STYLES: Record<ToastVariant, string> = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  error: 'border-red-200 bg-red-50 text-red-900',
  info: 'border-sky-200 bg-sky-50 text-sky-900',
};

function ToastMessage({ toast, onDismiss }: ToastProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const enterTimer = window.setTimeout(() => setVisible(true), 10);
    const dismissTimer = window.setTimeout(() => {
      setVisible(false);
      window.setTimeout(() => onDismiss(toast.id), 200);
    }, 4000);

    return () => {
      window.clearTimeout(enterTimer);
      window.clearTimeout(dismissTimer);
    };
  }, [onDismiss, toast.id]);

  return (
    <div
      className={`pointer-events-auto w-full max-w-sm rounded-xl border px-4 py-3 shadow-lg transition-all duration-200 ${
        VARIANT_STYLES[toast.variant]
      } ${visible ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'}`}
      role="status"
      aria-live="polite"
    >
      <p className="text-sm font-medium leading-5 text-current">{toast.message}</p>
    </div>
  );
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(0);

  const showToast = useCallback((message: string, variant: ToastVariant) => {
    nextId.current += 1;
    setToasts((current) => [
      ...current,
      {
        id: `toast-${nextId.current}`,
        message,
        variant,
      },
    ]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed right-4 top-4 z-50 flex w-full max-w-sm flex-col gap-3">
        {toasts.map((toast) => (
          <ToastMessage key={toast.id} toast={toast} onDismiss={dismissToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
